import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { PeerParticipant, PeerTypes } from '../../types/peers';
import { createCauseQueryString } from '../utils/createQueryString';
import { CreatePeerRequest, PeersResponse } from './types';
import { constructPeerFormData } from './utils';

type ParticipantRegistrationObject = {
  first_name: string;
  last_name: string;
  email: string;
  team_id?: string;
  options?: Record<string, string | string[]>;
  goal?: number;
};
interface ParticipantRegistrationRequest {
  participant: ParticipantRegistrationObject;
  causeId: string;
  campaignId: string;
}

type ResendParticipantRegistrationEmailRequest = {
  causeId: string;
  campaignId: string;
  uuids: string[];
};

type ResendParticipantRegistrationEmailResponse = {
  success: boolean;
  participants: PeerParticipant[];
};

/**
 * Create new participant - register
 */
function useRegisterParticipant() {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const createParticipant = async ({
    participant,
    causeId,
    campaignId,
  }: ParticipantRegistrationRequest): Promise<PeerParticipant> => {
    const causeQuery = createCauseQueryString(causeId);
    const { data } = await StoreApi.post(`/store_items/${campaignId}/participants?${causeQuery}`, {
      cause_id: causeId,
      participant,
    });
    return data.participant;
  };

  return useMutation({
    mutationFn: createParticipant,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const queryKey = storeKeys.id(causeId).items.campaigns.id(campaignId).participants.all;

      return queryClient.invalidateQueries({ queryKey });
    },
  });
}

const useCreateCampaignPeer = <T extends PeerTypes>({ peerType }: { peerType: T }) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const createPeer = async ({ causeId, campaignId, ...rest }: CreatePeerRequest<T>) => {
    if (!causeId || !campaignId) throw new Error('Missing required value');

    const form = constructPeerFormData<T>({ causeId, campaignId, peerType, request: rest });

    const { data } = await StoreApi.post<PeersResponse<T>>(
      `/store_items/${campaignId}/${peerType}`,
      form
    );
    return data[peerType == 'participants' ? 'participant' : 'team'];
  };
  return useMutation({
    mutationFn: createPeer,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key =
        campaignId && causeId
          ? storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].all
          : undefined;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

const useBulkResendParticipantRegistrationEmail = () => {
  const StoreApi = useStoreApi();

  const resendParticipantRegistrationEmail = async ({
    causeId,
    campaignId,
    uuids,
  }: ResendParticipantRegistrationEmailRequest) => {
    const { data } = await StoreApi.post<ResendParticipantRegistrationEmailResponse>(
      `/store_items/${campaignId}/participants/bulk/resend_registration_email`,
      {
        cause_id: causeId,
        store_item_id: campaignId,
        uuids,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: resendParticipantRegistrationEmail,
  });
};

export { useBulkResendParticipantRegistrationEmail, useCreateCampaignPeer, useRegisterParticipant };
